import { useState } from 'react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Combobox } from '@/components/ui/combobox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useUsers, useUserAccess, useGrantAccess, useRevokeAccess } from '@/hooks/use-team-access'
import { useAccounts } from '@/hooks/use-accounts'
import { fetchCapabilities } from '@/hooks/use-capabilities'
import { supabase } from '@/lib/supabase'
import { AdminNav } from '@/components/admin/admin-nav'
import { cn } from '@/lib/utils'
import type { Database } from '@/types/database'

type AccessRole = Database['public']['Enums']['access_role']
type UserRow = NonNullable<ReturnType<typeof useUsers>['data']>[number]

const ROLE_COLORS: Record<string, string> = {
  admin: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  user: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  read_only: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
}

export const Route = createFileRoute('/_authed/admin/team-access')({
  beforeLoad: async () => {
    // users.manage is the canonical capability for granting/revoking account
    // access (UI gating only — RLS remains the authorization boundary). A
    // parent-admitted user without it holds settings.manage, so /admin
    // forwards them to Modules — never a bounce loop.
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) throw redirect({ to: '/login' })

    const capabilities = await fetchCapabilities(user.id)
    if (!capabilities.canManageUsers) throw redirect({ to: '/admin' })
  },
  component: TeamAccessPage,
})

function TeamAccessPage() {
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const { data: users = [], isLoading: usersLoading } = useUsers()

  const selectedUser = users.find((u) => u.id === selectedUserId)

  return (
    <div className="p-6">
      {/* Slice 1B-1: "Admin" is titled by the ONE shared header. */}
      <AdminNav />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl">
        {/* User list */}
        <div>
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
            Users ({users.length})
          </h2>
          {usersLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : (
            <div className="divide-y border rounded-lg overflow-hidden">
              {users.map((user) => (
                <UserRow
                  key={user.id}
                  user={user}
                  selected={user.id === selectedUserId}
                  onClick={() => setSelectedUserId(user.id === selectedUserId ? null : user.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Access panel */}
        <div>
          {selectedUser ? (
            <AccessPanel user={selectedUser} />
          ) : (
            <div className="flex items-center justify-center h-40 border rounded-lg bg-muted/30">
              <p className="text-sm text-muted-foreground">Select a user to manage their account access</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function UserRow({
  user,
  selected,
  onClick,
}: {
  user: UserRow
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center justify-between px-4 py-3 text-left transition-colors hover:bg-muted/50',
        selected && 'bg-muted',
      )}
    >
      <div>
        <p className="text-sm font-medium">{user.full_name ?? '—'}</p>
        <p className="text-xs text-muted-foreground">{user.email}</p>
      </div>
      <div className="flex items-center gap-2">
        <Badge
          variant="secondary"
          className={`text-xs font-normal ${ROLE_COLORS[user.role] ?? ''}`}
        >
          {user.role}
        </Badge>
        {!user.is_active && (
          <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
            inactive
          </Badge>
        )}
      </div>
    </button>
  )
}

function AccessPanel({ user }: { user: UserRow }) {
  const [selectedAccountId, setSelectedAccountId] = useState('')
  const [selectedRole, setSelectedRole] = useState<AccessRole>('collaborator')
  const { data: accessList = [], isLoading } = useUserAccess(user.id)
  const { data: accounts = [] } = useAccounts()
  const grantAccess = useGrantAccess()
  const revokeAccess = useRevokeAccess()

  const grantedAccountIds = new Set(accessList.map((a) => a.account_id))
  const availableAccounts = accounts.filter((a) => !grantedAccountIds.has(a.id))
  const accountOptions = availableAccounts.map((a) => ({ value: a.id, label: a.name }))

  const handleGrant = async () => {
    if (!selectedAccountId) return
    await grantAccess.mutateAsync({
      accountId: selectedAccountId,
      userId: user.id,
      role: selectedRole,
    })
    setSelectedAccountId('')
  }

  return (
    <div className="space-y-4">
      <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
        Account access — {user.full_name ?? user.email}
      </h2>

      {isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : accessList.length === 0 ? (
        <p className="text-sm text-muted-foreground">No account access granted.</p>
      ) : (
        <div className="divide-y border rounded-lg overflow-hidden">
          {accessList.map((entry) => {
            type EntryWithAccount = typeof entry & {
              account?: { id: string; name: string; type: string } | null
            }
            const acct = (entry as EntryWithAccount).account
            return (
              <div key={entry.id} className="flex items-center justify-between px-4 py-3">
                <div>
                  <p className="text-sm font-medium">{acct?.name ?? entry.account_id}</p>
                  <p className="text-xs text-muted-foreground capitalize">{acct?.type?.replace('_', ' ')}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="capitalize font-normal text-xs">
                    {entry.access_role}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-destructive hover:text-destructive"
                    onClick={() =>
                      void revokeAccess.mutate({
                        id: entry.id,
                        userId: user.id,
                        accountId: entry.account_id,
                      })
                    }
                  >
                    Revoke
                  </Button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <form
        onSubmit={(e) => { e.preventDefault(); void handleGrant() }}
        className="space-y-2 p-4 border rounded-lg bg-muted/40"
      >
        <h4 className="text-sm font-medium">Grant access to account</h4>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Combobox
              options={accountOptions}
              value={selectedAccountId || undefined}
              onChange={(v) => setSelectedAccountId(v ?? '')}
              placeholder="Select account…"
              searchPlaceholder="Search accounts…"
            />
          </div>
          <Select value={selectedRole} onValueChange={(v) => setSelectedRole(v as AccessRole)}>
            <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="owner">Owner</SelectItem>
              <SelectItem value="collaborator">Collaborator</SelectItem>
              <SelectItem value="view_only">View only</SelectItem>
            </SelectContent>
          </Select>
          <Button type="submit" size="sm" disabled={!selectedAccountId || grantAccess.isPending}>
            Grant
          </Button>
        </div>
      </form>
    </div>
  )
}
